import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Agent } from '@/hooks/useAgents';
import { PartialAsset } from '@/hooks/useAssets';
import { Capability } from '@/hooks/useCapabilities';
import {
  DRAWER_TYPES,
  type DrawerType,
  MAX_OPEN_DRAWERS,
} from '@/state/drawer.constants';
import {
  HandleOpenAssetDrawerOptions,
  resolveAssetDrawerResource,
} from '@/state/resolveAssetDrawerResource';
import {
  Account,
  IntegrationMeta,
  ParkedDomain,
  Preseed,
  Risk,
  Seed,
  Technology,
  Vulnerability,
} from '@/types';
import { safeExecute } from '@/utils/function.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

interface UseModalState {
  open: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface GlobalState {
  modal: {
    allModal: UseModalState;
    seed: UseModalState;
    risk: {
      open: boolean;
      type: 'risk' | 'material' | 'selectorScreen';
      onChange: (
        open: boolean,
        type: GlobalState['modal']['risk']['type']
      ) => void;
      selectedAssets: string[];
      onSelectedAssetsChange: React.Dispatch<React.SetStateAction<string[]>>;
      selectedRisks: string[];
      onSelectedRisksChange: React.Dispatch<React.SetStateAction<string[]>>;
    };
    selectUserAgent: {
      assetKeys: string[];
      onAssetKeysChange: React.Dispatch<React.SetStateAction<string[]>>;
    };
    asset: UseModalState;
    file: UseModalState;
    upgrade: UseModalState;
    pushNotification: UseModalState;
    surfaceSetup: UseModalState;
    alert: UseModalState;
    ticket: {
      associateTicket: UseModalState & {
        riskKeys: string[];
        onRiskKeysChange: React.Dispatch<React.SetStateAction<string[]>>;
      };
      setup: UseModalState;
    };
  };
  awsMarketplaceConfig: {
    value?: Record<string, string>;
    onChange: React.Dispatch<GlobalState['awsMarketplaceConfig']['value']>;
    verifyLinking: boolean;
    onVerifyLinkingChange: React.Dispatch<React.SetStateAction<boolean>>;
  };
  azureMarketplaceConfig: {
    value?: Record<string, string>;
    onChange: React.Dispatch<GlobalState['azureMarketplaceConfig']['value']>;
    verifyLinking: boolean;
    onVerifyLinkingChange: React.Dispatch<React.SetStateAction<boolean>>;
  };
  riskNotification: {
    value: { message: string } | null;
    onChange: (value: { message: string } | null) => void;
  };
  setupIntegration: {
    value: Account | undefined;
    onChange: React.Dispatch<React.SetStateAction<Account | undefined>>;
  };
  dashboard: {
    period: string;
  };
  drawerState: {
    drawerOrder: DrawerType[];
    agent?: Agent;
    vulnerability?: Risk;
    seed?: Seed;
    integration?: IntegrationMeta;
    threatIntel?: Vulnerability;
    preseed?: Preseed;
    capability?: Capability;
    customer?: import('@/sections/customerManagement/types').CustomerData;
    parkedDomain?: ParkedDomain;

    // Properties
    agentDrawerKey?: string;
    agentDrawerTab?: string;
    assetDrawerTab?: string;
    assetDrawerSubTab?: string;
    assetDrawerKey?: string;
    technologyDrawerKey?: string;
    technologyDrawerTab?: string;
    vulnerabilityDrawerTab?: string;
    vulnerabilityDrawerKey?: string;
    seedDrawerKey?: string;
    seedDrawerTab?: string;
    integrationDrawerKey?: string;
    integrationDrawerCategory?: string;
    notificationsDrawerOpen?: boolean;
    notificationsDrawerFilterType?: 'email' | 'webhook' | 'integration';
    exposuresDrawerOpen?: boolean;
    threatIntelDrawerKey?: string;
    preseedDrawerKey?: string;
    preseedDrawerTab?: string;
    capabilityDrawerKey?: string;
    capabilityDrawerTab?: string;
    customerDrawerKey?: string;
    customerDrawerTab?: string;
    parkedDomainDrawerKey?: string;

    // Setters
    setAgent: (agent?: Agent) => void;
    setAgentDrawerTab: (agentDrawerTab: string) => void;
    setAssetDrawerTab: (assetDrawerTab: string) => void;
    setTechnologyDrawerTab: (technologyDrawerTab: string) => void;
    setVulnerability: (vulnerability?: Risk) => void;
    setVulnerabilityDrawerTab: (vulnerabilityDrawerTab: string) => void;
    setSeed: (seed?: Seed) => void;
    setSeedDrawerTab: (seedDrawerTab: string) => void;
    setIntegration: (integration?: IntegrationMeta) => void;
    setIntegrationDrawerCategory: (category: string) => void;
    setNotificationsDrawerOpen: (
      notificationsDrawerOpen: boolean,
      filterType?: 'email' | 'webhook' | 'integration'
    ) => void;
    setExposuresDrawerOpen: (exposuresDrawerOpen: boolean) => void;
    setPreseed: (preseed?: Preseed) => void;
    setThreatIntel: (threatIntel?: Vulnerability) => void;
    setCapability: (capability?: Capability) => void;
    setCustomer: (
      customer?: import('@/sections/customerManagement/types').CustomerData
    ) => void;
    setCustomerDrawerTab: (customerDrawerTab: string) => void;
    setParkedDomain: (parkedDomain?: ParkedDomain) => void;

    // Handlers
    handleOpenAgentDrawer: (
      agent?: Agent,
      options?: { initialTab?: string }
    ) => void;
    handleOpenTechnologyDrawer: (
      technology?: Technology,
      osint?: boolean
    ) => void;
    handleOpenAssetDrawer: (
      asset?: PartialAsset,
      isActiveDirectory?: boolean,
      osint?: boolean,
      options?: HandleOpenAssetDrawerOptions
    ) => void;
    handleOpenVulnerabilityDrawer: (
      vulnerability?: Risk,
      doNotSetVulnerability?: boolean,
      osint?: boolean,
      initialTab?: string
    ) => void;
    handleOpenSeedDrawer: (seed?: Seed) => void;
    handleOpenIntegrationDrawer: (
      integration?: IntegrationMeta,
      category?: string
    ) => void;
    handleOpenThreatIntelDrawer: (threatIntel?: Vulnerability) => void;
    handleOpenPreseedDrawer: (preseed?: Preseed) => void;
    handleOpenCapabilityDrawer: (capability?: Capability) => void;
    handleOpenCustomerDrawer: (
      customer?: import('@/sections/customerManagement/types').CustomerData,
      options?: { initialTab?: string }
    ) => void;
    handleOpenParkedDomainDrawer: (parkedDomain?: ParkedDomain) => void;
  };
  lastVisitedUserId: string;
  setLastVisitedUserId: React.Dispatch<React.SetStateAction<string>>;
  /**
   * POE (Proof-of-Exploit) drawer mode flag. A standalone global-state field,
   * backed by the `poeEnabled`/`setPOEEnabled` useState below. (ENG-43 removed
   * the legacy `keyboardListeners` event bus; this flag is now the sole consumer
   * of that state.)
   */
  poeMode: {
    enabled: boolean;
    setEnabled: (active: boolean) => void;
  };
}

const GlobalStateContext = createContext<GlobalState | undefined>(undefined);

/**
 * Cap guard shared by every drawerOrder write path. Returns true (and shows
 * the cap toast) when `order` — the candidate POST-dedupe next drawerOrder —
 * would exceed MAX_OPEN_DRAWERS. Deliberately called INSIDE the functional
 * setSearchParams updater so the guard and the writes consume the same
 * `searchParams` snapshot atomically within one updater call — a stale
 * snapshot can only make a racing open lose (get dropped), never overshoot
 * the cap. (A pre-setter check read a stale render closure, so rapid
 * successive opens bypassed it.) Because it runs on the post-dedupe order,
 * reopening an already-open drawer type at cap proceeds (the stack does not
 * grow) instead of toasting. The stable toast id makes sonner dedupe the
 * warning when the user rapidly repeats a blocked open (e.g. repeated
 * clicks), so they see one toast rather than a stack of them.
 */
function exceedsDrawerCap(order: string[]): boolean {
  if (order.length <= MAX_OPEN_DRAWERS) {
    return false;
  }
  toast.warning('Please close a drawer before opening another', {
    id: 'drawer-cap',
    description: `Only ${MAX_OPEN_DRAWERS} drawers can be open at a time`,
    duration: 3000,
  });
  return true;
}

/**
 * Parse + validate the `drawerOrder` URL param — the only way drawerOrder is
 * read. The param is user-editable (hand-crafted URLs, stale bookmarks, tokens
 * from removed drawer types such as the deleted `queryResults`), and an
 * unvalidated token is invisible-but-counted: the drawers renderer shows
 * nothing for it, yet it still inflates the length the cap guard sees, so a
 * user staring at two drawers gets "close a drawer" toasts. Guards: non-array
 * JSON parses to []; entries are filtered to DRAWER_TYPES; duplicates keep the
 * LAST occurrence (drawerOrder is append-to-top, so the last occurrence is the
 * authoritative stack position). Every write path re-serializes from this
 * parse, so the next accepted mutation self-heals a stale URL.
 */
function parseDrawerOrder(searchParams: URLSearchParams): DrawerType[] {
  const raw = safeExecute<unknown>(
    () => JSON.parse(searchParams.get('drawerOrder') || '[]'),
    []
  );
  if (!Array.isArray(raw)) {
    return [];
  }
  const known = raw.filter((entry): entry is DrawerType =>
    (DRAWER_TYPES as readonly unknown[]).includes(entry)
  );
  // Keep-LAST dedupe in a single reverse scan: walking from the end, the
  // first time a type is seen IS its last occurrence; reversing at the end
  // restores the original relative order of those survivors.
  const seen = new Set<DrawerType>();
  const deduped: DrawerType[] = [];
  for (let i = known.length - 1; i >= 0; i--) {
    if (!seen.has(known[i])) {
      seen.add(known[i]);
      deduped.push(known[i]);
    }
  }
  return deduped.reverse();
}

/**
 * Companion URL params deleted alongside a pruned drawerOrder token —
 * mirroring what each opener's close path deletes (`[drawerKey, ...tabKeys]`
 * in handleSetSearchParams) so a mount-time prune leaves the URL exactly as a
 * real close would. 'integration' is absent by design: its prune keeps
 * drawerCompositeKey (see the mount effect) and deletes only
 * integrationDrawerCategory. Exhaustive over every other DrawerType, so
 * adding a drawer type forces a decision here.
 */
const PRUNE_COMPANION_PARAMS: Record<
  Exclude<DrawerType, 'integration'>,
  readonly string[]
> = {
  agent: ['agentDrawerTab'],
  asset: ['assetDrawerTab', 'assetDrawerSubTab', 'historyId', 'assetOsint'],
  vulnerability: ['vulnerabilityDrawerTab', 'vulnerabilityOsint'],
  seed: ['seedDrawerTab'],
  threatIntel: ['threat-intel-drawer-tab', 'cvss-version-selection'],
  preseed: ['preseedDrawerTab'],
  technology: ['technologyDrawerTab', 'technologyOsint'],
  capability: ['capabilityDrawerTab'],
  customer: ['customerDrawerTab'],
  parkedDomain: [],
};

/**
 * Mount-time orphan decision for the one-time prune effect in
 * GlobalStateProvider: which tokens in `order` have a dead render gate in
 * `params`. 'customer' is always orphaned at mount (its gate is an in-memory
 * object no reload survives), 'integration' unless the URL carries
 * drawerCompositeKey=integration (the one param AppDrawers re-seeds it from),
 * and every other type when its companion `<type>DrawerKey` param — the
 * render gate in sections/drawers/index.tsx — is absent or empty.
 */
function getOrphanedDrawerTypes(
  order: DrawerType[],
  params: URLSearchParams
): Set<DrawerType> {
  const orphaned = new Set<DrawerType>();
  order.forEach(type => {
    if (type === 'customer') {
      orphaned.add(type);
    } else if (type === 'integration') {
      if (params.get(StorageKey.DRAWER_COMPOSITE_KEY) !== 'integration') {
        orphaned.add(type);
      }
    } else if (!params.get(`${type}DrawerKey`)) {
      orphaned.add(type);
    }
  });
  return orphaned;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider');
  }
  return context;
};

export const GlobalStateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const period = '90';

  const [allModalOpen, setAllModalOpen] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);
  const [riskType, setRiskType] =
    useState<GlobalState['modal']['risk']['type']>('selectorScreen');

  const [selectUserAgentAssetKeys, setSelectUserAgentAssetKeys] = useState<
    string[]
  >([]);

  const [assetOpen, setAssetOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [pushNotificationOpen, setPushNotificationOpen] = useState(false);
  const [surfaceSetupOpen, setSurfaceSetupOpen] = useState(false);
  const [integration, setIntegration] = useState<IntegrationMeta>();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [setupIntegration, setSetupIntegration] = useState<Account>();
  const [poeEnabled, setPOEEnabled] = useState(false);

  const [riskNotification, setRiskNotification] = useState<{
    message: string;
  } | null>(null);

  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [selectedRisks, setSelectedRisks] = useState<string[]>([]);

  const [awsMarketplaceConfig, setAwsMarketplaceConfig] = useStorage<
    GlobalState['awsMarketplaceConfig']['value']
  >({
    key: StorageKey.AWS_MARKETPLACE_CONFIG,
  });
  const [verifyLinking, setVerifyLinking] = useStorage<
    GlobalState['awsMarketplaceConfig']['verifyLinking']
  >(
    {
      key: StorageKey.AWS_MARKETPLACE_CONFIG_VERIFY_LINKING,
    },
    false
  );
  const [azureMarketplaceConfig, setAzureMarketplaceConfig] = useStorage<
    GlobalState['azureMarketplaceConfig']['value']
  >({
    key: StorageKey.AZURE_MARKETPLACE_CONFIG,
  });
  const [azureVerifyLinking, setAzureVerifyLinking] = useStorage<
    GlobalState['azureMarketplaceConfig']['verifyLinking']
  >(
    {
      key: StorageKey.AZURE_MARKETPLACE_CONFIG_VERIFY_LINKING,
    },
    false
  );
  const [searchParams, setSearchParams] = useSearchParams();

  // Mount-time prune of drawerOrder tokens whose render gate is dead (C2 +
  // F-A, PR #7304 review). A token only renders when its gate in
  // sections/drawers/index.tsx holds, and every gate can be dead at mount:
  //  - 'customer': CustomerDrawer renders drawerState.customer, which only
  //    exists after handleOpenCustomerDrawer runs in this session — at mount
  //    it is ALWAYS undefined, so a reloaded 'customer' token can never
  //    render again yet still consumes a cap slot.
  //  - 'integration': self-heals ONLY when the URL also carries
  //    drawerCompositeKey=integration (AppDrawers re-seeds the object from
  //    that param); without it the token is equally dead.
  //  - every other type gates on its companion `<type>DrawerKey` URL param,
  //    so a hand-crafted or truncated URL can carry a token with no key: it
  //    renders nothing yet still counts toward the cap and flips
  //    HotkeyScopeSync's overlay-open computation.
  // parseDrawerOrder's unknown-token filtering cannot see this gap — these
  // are VALID tokens whose gate is dead — so this one-time effect closes it,
  // deleting each pruned type's companion params with the token.
  //
  // Scope of the empty dep array: no write THROUGH THIS PROVIDER can create
  // the orphan condition mid-session, because every opener here sets the
  // object state / companion key in the same write as the token. That is a
  // statement about this provider, NOT about the URL — anything else that
  // writes drawerOrder opens a dead token this effect will not run again to
  // heal, so it survives until the next reload. The load-bearing invariant is
  // therefore NOT "these params are written only by the handlers below":
  // several call sites outside this provider do write drawerOrder, and all of
  // them are safe because each seeds that type's render gate — the companion
  // `<type>DrawerKey`, or `drawerCompositeKey` for 'integration' — in the SAME
  // write as the token. That is the property to preserve: every writer of
  // drawerOrder, here or elsewhere, sets that type's gate atomically with the
  // token. The two producers ENG-5571 removed broke it in the one way no URL
  // write can repair — they emitted a 'customer' token, whose gate is the
  // in-memory drawerState.customer that only handleOpenCustomerDrawer sets, so
  // no URL param could ever satisfy it. If a writer ever lands that cannot
  // seed its gate atomically, this must become a subscription, not a mount
  // effect.
  useEffect(() => {
    const orphaned = getOrphanedDrawerTypes(
      parseDrawerOrder(searchParams),
      searchParams
    );
    if (orphaned.size === 0) {
      return; // nothing orphaned — leave the URL untouched
    }
    setSearchParams(
      params => {
        // Re-parse and re-decide from the FRESH params (functional-updater
        // contract) — the reads above were only the do-nothing gate.
        const freshOrder = parseDrawerOrder(params);
        const freshOrphaned = getOrphanedDrawerTypes(freshOrder, params);
        const pruned = freshOrder.filter(type => !freshOrphaned.has(type));
        if (pruned.length === 0) {
          params.delete('drawerOrder');
        } else {
          params.set('drawerOrder', JSON.stringify(pruned));
        }
        freshOrphaned.forEach(type => {
          if (type === 'integration') {
            // Keep DRAWER_COMPOSITE_KEY: this branch only runs when it is NOT
            // 'integration', so it belongs to something else.
            params.delete('integrationDrawerCategory');
            return;
          }
          // The key param is deleted defensively: absent for most orphans
          // (that IS the keyed orphan condition) but present-and-empty is
          // also an orphan, and customer's key can survive its dead object.
          params.delete(`${type}DrawerKey`);
          PRUNE_COMPANION_PARAMS[type].forEach(param => params.delete(param));
        });
        return params;
      },
      { replace: true }
    );
    // One-time mount effect by design (exhaustive-deps is off project-wide;
    // the React Compiler handles memoization).
  }, []);

  const drawerOrder = parseDrawerOrder(searchParams);
  const agentDrawerKey = searchParams.get('agentDrawerKey') || '';
  const agentDrawerTab = searchParams.get('agentDrawerTab') || '';
  const assetDrawerTab = searchParams.get('assetDrawerTab') || '';
  const technologyDrawerTab = searchParams.get('technologyDrawerTab') || '';
  const assetDrawerSubTab = searchParams.get('assetDrawerSubTab') || '';
  const assetDrawerKey = searchParams.get('assetDrawerKey') || '';
  const technologyDrawerKey = searchParams.get('technologyDrawerKey') || '';
  const vulnerabilityDrawerTab =
    searchParams.get('vulnerabilityDrawerTab') || '';
  const vulnerabilityDrawerKey =
    searchParams.get('vulnerabilityDrawerKey') || '';
  const threatIntelDrawerKey = searchParams.get('threatIntelDrawerKey') || '';
  const preseedDrawerKey = searchParams.get('preseedDrawerKey') || '';
  const preseedDrawerTab = searchParams.get('preseedDrawerTab') || '';
  const seedDrawerKey = searchParams.get('seedDrawerKey') || '';
  const seedDrawerTab = searchParams.get('seedDrawerTab') || '';
  const capabilityDrawerKey = searchParams.get('capabilityDrawerKey') || '';
  const capabilityDrawerTab = searchParams.get('capabilityDrawerTab') || '';
  const customerDrawerKey = searchParams.get('customerDrawerKey') || '';
  const customerDrawerTab = searchParams.get('customerDrawerTab') || '';
  const parkedDomainDrawerKey = searchParams.get('parkedDomainDrawerKey') || '';

  const [associateTicketRiskKeys, setAssociateTicketRiskKeys] = useState<
    string[]
  >([]);
  const [associateTicketOpen, setAssociateTicketOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);

  const [agent, setAgent] = useState<Agent>();
  const [vulnerability, setVulnerability] = useState<Risk>();
  const [seed, setSeed] = useState<Seed>();
  const [threatIntel, setThreatIntel] = useState<Vulnerability>();
  const [preseed, setPreseed] = useState<Preseed>();
  const [capability, setCapability] = useState<Capability>();
  const [customer, setCustomer] =
    useState<import('@/sections/customerManagement/types').CustomerData>();
  const [parkedDomain, setParkedDomain] = useState<ParkedDomain>();
  const [notificationsDrawerOpen, setNotificationsDrawerOpenState] =
    useState(false);
  const [notificationsDrawerFilterType, setNotificationsDrawerFilterType] =
    useState<'email' | 'webhook' | 'integration' | undefined>(undefined);
  const [exposuresDrawerOpen, setExposuresDrawerOpen] = useState(false);

  // Wrapper to set both drawer open state and filter type
  const setNotificationsDrawerOpen = (
    open: boolean,
    filterType?: 'email' | 'webhook' | 'integration'
  ) => {
    setNotificationsDrawerOpenState(open);
    setNotificationsDrawerFilterType(open ? filterType : undefined);
  };
  const [lastVisitedUserId, setLastVisitedUserId] = useStorage<string>(
    {
      key: StorageKey.LAST_VISITED_USER_ID,
    },
    ''
  );

  function handleSetSearchParams({
    drawerType,
    tabKeys = [],
    resource,
    defaultValues = {},
  }: {
    drawerType:
      | 'agent'
      | 'asset'
      | 'vulnerability'
      | 'seed'
      | 'threatIntel'
      | 'preseed'
      | 'technology'
      | 'capability'
      | 'customer'
      | 'parkedDomain';
    tabKeys?: string[];
    resource?: { key: string };
    defaultValues?: Record<string, string>;
  }): boolean {
    const drawerKey = `${drawerType}DrawerKey`;

    // Whether the URL update was actually applied (false when the cap guard
    // rejected the open). Callers use this to skip retained object-state
    // updates for a drawer that never opened. Reading the flag right after
    // setSearchParams returns relies on react-router evaluating a functional
    // updater SYNCHRONOUSLY within the call — a constraint that isn't visible
    // from this code.
    let applied = true;

    setSearchParams(
      searchParams => {
        // Read FRESH drawerOrder from the searchParams parameter to avoid a
        // stale closure. Freshness caveat: the functional updater is fresh
        // across renders and rapid user events, but two setSearchParams calls
        // in the SAME tick each receive the same pre-update params — last
        // write wins and the earlier open is silently lost. Every current
        // opener is user-event driven, so this stays latent; do NOT batch two
        // drawer opens in one tick.
        const currentDrawerOrder = parseDrawerOrder(searchParams);

        if (resource) {
          // Append to the end of the stack (dedupe first so reopening an
          // already-open type moves it to the top instead of growing the order)
          const newDrawerOrder = [
            ...currentDrawerOrder.filter(drawer => drawer !== drawerType),
            drawerType,
          ];

          // The cap check lives HERE — inside the functional updater, after the
          // post-dedupe newDrawerOrder is computed and BEFORE any mutation — so
          // it reads fresh params (the old pre-setter check read a stale render
          // closure and rapid successive opens could overshoot the cap) and so
          // reopening an already-open type at cap still proceeds (the order
          // does not grow). Past the cap: toast, mutate nothing, and return the
          // previous params unchanged.
          if (exceedsDrawerCap(newDrawerOrder)) {
            applied = false;
            return searchParams;
          }

          searchParams.set(drawerKey, resource.key);
          searchParams.set('drawerOrder', JSON.stringify(newDrawerOrder));

          // Preserve existing tab params if not explicitly overridden in defaultValues
          // This prevents tab oscillation when navigating between rows with arrow keys
          tabKeys.forEach(key => {
            if (defaultValues?.[key] !== undefined) {
              searchParams.set(key, defaultValues[key]);
            } else {
              const existing = searchParams.get(key);
              // Not provided - preserve existing value if present, or default to empty string
              searchParams.set(key, existing || '');
            }
          });
        } else {
          [drawerKey, ...tabKeys].forEach(key => {
            searchParams.delete(key);
          });
          searchParams.set(
            'drawerOrder',
            JSON.stringify(
              currentDrawerOrder.filter(drawer => drawer !== drawerType)
            )
          );
        }

        return searchParams;
      },
      { replace: true }
    );

    return applied;
  }

  function handleOpenAgentDrawer(
    agent?: Agent,
    options?: { initialTab?: string }
  ) {
    const applied = handleSetSearchParams({
      drawerType: 'agent',
      tabKeys: ['agentDrawerTab'],
      resource: agent ? { key: agent.client_id } : undefined,
      defaultValues: options?.initialTab
        ? { agentDrawerTab: options.initialTab }
        : {},
    });
    // Only retain the object for a drawer that actually opened — a cap-blocked
    // open must not leave stale state that a later open (e.g. via history
    // navigation) would briefly display. The close path always applies, so
    // closing still clears the object.
    if (applied) {
      setAgent(agent);
    }
  }

  /**
   * Opens or closes the technology drawer
   *
   * CRITICAL: tabKeys is empty when opening to preserve sticky tab behavior.
   * When navigating between rows with keyboard, the tab should stay on the
   * user's selected tab, not reset. tabKeys only included when closing to
   * properly delete the tab param from URL.
   *
   * @param technology - Technology to open drawer for, or undefined to close
   */
  function handleOpenTechnologyDrawer(
    technology?: Technology,
    osint?: boolean
  ) {
    handleSetSearchParams({
      drawerType: 'technology',
      // Only include tabKeys when CLOSING drawer (technology is undefined)
      // When OPENING drawer, leave tab param alone for sticky tab behavior
      tabKeys: technology
        ? ['technologyOsint']
        : ['technologyDrawerTab', 'technologyOsint'],
      resource: technology,
      defaultValues: osint ? { technologyOsint: 'true' } : {},
    });
  }

  function handleOpenAssetDrawer(
    asset?: PartialAsset,
    isActiveDirectory?: boolean,
    osint?: boolean,
    options?: HandleOpenAssetDrawerOptions
  ) {
    // For Active Directory entities, set specific tab defaults
    const defaultTabs: Record<string, string> = isActiveDirectory
      ? {
          assetDrawerTab: 'attributes', // Default to Attributes tab
          assetDrawerSubTab: 'activeDirectory', // Default to Active Directory sub-tab
          // The Classification view will be the default within the Active Directory section
        }
      : {};

    if (osint) {
      defaultTabs['assetOsint'] = 'true';
    }

    // Rollup Step 1 (item B): resolve member observations *up* to their
    // parent :Application so every entry point (main table row, search
    // result, etc.) opens the unified Application drawer rather than a
    // member sub-drawer. Drill-down call sites (e.g. the members table
    // inside the Application drawer) opt out via `{ resolveToGroup: false }`.
    const resource = resolveAssetDrawerResource(asset, options);

    handleSetSearchParams({
      drawerType: 'asset',
      tabKeys: [
        'assetDrawerTab',
        'assetDrawerSubTab',
        'historyId',
        'assetOsint',
      ],
      resource,
      defaultValues: defaultTabs,
    });
  }

  function handleOpenVulnerabilityDrawer(
    vulnerability?: Risk,
    doNotSetVulnerability?: boolean, // Kept for backward compatibility
    osint?: boolean,
    initialTab?: string
  ) {
    // KEY-BASED PATTERN: URL param is the single source of truth.
    // VulnerabilityDrawer fetches data internally via useVulnerabilityDrawerData(key).
    // REMOVED: setVulnerability(vulnerability) to eliminate race conditions.
    // `initialTab` flows through tabKeys.forEach to set vulnerabilityDrawerTab in
    // the SAME setSearchParams call as the open (one functional setter, no race).
    handleSetSearchParams({
      drawerType: 'vulnerability',
      tabKeys: ['vulnerabilityDrawerTab', 'vulnerabilityOsint'],
      resource: vulnerability,
      defaultValues: {
        ...(osint ? { vulnerabilityOsint: 'true' } : {}),
        ...(initialTab ? { vulnerabilityDrawerTab: initialTab } : {}),
      },
    });
  }

  function handleOpenSeedDrawer(seed?: Seed) {
    // KEY-BASED PATTERN: URL param (seedDrawerKey) is the single source of truth.
    // The SeedDrawer component fetches seed data internally via useSeedDrawerData(seedDrawerKey).
    // We no longer call setSeed() here to avoid race conditions when clicking quickly between rows.
    handleSetSearchParams({
      drawerType: 'seed',
      tabKeys: ['seedDrawerTab'],
      resource: seed,
    });
  }

  function handleOpenThreatIntelDrawer(threatIntel?: Vulnerability) {
    const applied = handleSetSearchParams({
      drawerType: 'threatIntel',
      resource: threatIntel,
      tabKeys: ['threat-intel-drawer-tab', 'cvss-version-selection'],
    });
    // See handleOpenAgentDrawer: no stale object state for a cap-blocked open.
    if (applied) {
      setThreatIntel(threatIntel);
    }
  }

  function handleOpenPreseedDrawer(preseed?: Preseed) {
    // KEY-BASED PATTERN: URL param is the single source of truth.
    // PreseedDrawer fetches data internally via usePreseedDrawerData(key).
    // REMOVED: setPreseed(preseed) to eliminate race conditions.
    handleSetSearchParams({
      drawerType: 'preseed',
      tabKeys: ['preseedDrawerTab'],
      resource: preseed,
    });
  }

  function handleOpenCapabilityDrawer(capability?: Capability) {
    // KEY-BASED PATTERN: URL param is the single source of truth.
    // CapabilityDrawer fetches data internally via useCapabilityDrawerData(key).
    // REMOVED: setCapability(capability) to eliminate race conditions.
    handleSetSearchParams({
      drawerType: 'capability',
      tabKeys: ['capabilityDrawerTab'],
      resource: capability ? { key: capability.name } : undefined,
    });
  }

  function handleOpenCustomerDrawer(
    customer?: import('@/sections/customerManagement/types').CustomerData,
    options?: { initialTab?: string }
  ) {
    const applied = handleSetSearchParams({
      drawerType: 'customer',
      tabKeys: ['customerDrawerTab'],
      resource: customer ? { key: customer.email } : undefined,
      defaultValues: options?.initialTab
        ? { customerDrawerTab: options.initialTab }
        : {},
    });

    // See handleOpenAgentDrawer: no stale object state for a cap-blocked open.
    // Especially important here — CustomerDrawer renders drawerState.customer
    // as its data source, so a stale object shows the wrong customer outright.
    if (applied) {
      setCustomer(customer);
    }
  }

  function handleOpenParkedDomainDrawer(parkedDomain?: ParkedDomain) {
    // KEY-BASED PATTERN: URL param is the single source of truth.
    // ParkedDomainDrawer fetches data internally via useParkedDomainDrawerData(key).
    // REMOVED: setParkedDomain(parkedDomain) to eliminate race conditions.
    handleSetSearchParams({
      drawerType: 'parkedDomain',
      tabKeys: [],
      resource: parkedDomain,
    });
  }

  function handleOpenIntegrationDrawer(
    integration?: IntegrationMeta,
    category?: string
  ) {
    if (category === undefined) {
      // Closing case
      setSearchParams(
        searchParams => {
          // Read FRESH drawerOrder from the searchParams parameter to avoid stale closure
          const currentDrawerOrder = parseDrawerOrder(searchParams);

          searchParams.delete(StorageKey.DRAWER_COMPOSITE_KEY);
          searchParams.delete('integrationDrawerCategory');
          // Remove integration from drawer order instead of deleting entirely
          searchParams.set(
            'drawerOrder',
            JSON.stringify(
              currentDrawerOrder.filter(drawer => drawer !== 'integration')
            )
          );
          return searchParams;
        },
        { replace: true }
      );
      setIntegration(undefined);
    } else {
      // Opening case (including default view)
      // Same captured-flag pattern as handleSetSearchParams: readable right
      // after setSearchParams returns because react-router evaluates a
      // functional updater synchronously within the call.
      let applied = true;
      setSearchParams(
        searchParams => {
          // Read FRESH drawerOrder from the searchParams parameter to avoid stale closure
          const currentDrawerOrder = parseDrawerOrder(searchParams);

          const newDrawerOrder = [
            ...currentDrawerOrder.filter(drawer => drawer !== 'integration'),
            'integration',
          ];

          // Same in-updater cap guard as handleSetSearchParams: checked on the
          // post-dedupe order BEFORE any mutation, so past the cap the drawer
          // does not open and the previous params are returned unchanged.
          // (If 'integration' is already open the order does not grow, so the
          // guard passes.)
          if (exceedsDrawerCap(newDrawerOrder)) {
            applied = false;
            return searchParams;
          }

          searchParams.set(StorageKey.DRAWER_COMPOSITE_KEY, 'integration');
          if (category) {
            searchParams.set('integrationDrawerCategory', category);
          }
          searchParams.set('drawerOrder', JSON.stringify(newDrawerOrder));
          return searchParams;
        },
        { replace: true }
      );
      // Only retain the object for a drawer that actually opened — state must
      // not be updated for a cap-blocked open, or it lingers as stale data for
      // a later open of the integration drawer.
      if (applied) {
        setIntegration({} as IntegrationMeta);
      }
    }
  }

  /**
   * Internal helper for setting drawer tab and key values using functional setter pattern.
   *
   * CRITICAL: This uses the functional setter pattern to avoid stale closure issues
   * that cause tab alternation when mixing mouse clicks and keyboard navigation.
   *
   * @see https://reactrouter.com/en/main/hooks/use-search-params - Functional updates
   * @param paramName - The URL param name (e.g., 'seedDrawerTab', 'assetDrawerKey')
   * @param value - The value to set
   */
  function setDrawerTabInternal(paramName: string, value: string) {
    setSearchParams(
      params => {
        params.set(paramName, value);
        return params;
      },
      { replace: true }
    );
  }

  function setAgentDrawerTab(tab: string) {
    setDrawerTabInternal('agentDrawerTab', tab);
  }

  function setSeedDrawerTab(tab: string) {
    setDrawerTabInternal('seedDrawerTab', tab);
  }

  function setAssetDrawerTab(tab: string) {
    setDrawerTabInternal('assetDrawerTab', tab);
  }

  function setTechnologyDrawerTab(tab: string) {
    setDrawerTabInternal('technologyDrawerTab', tab);
  }

  function setVulnerabilityDrawerTab(tab: string) {
    setDrawerTabInternal('vulnerabilityDrawerTab', tab);
  }

  function setCustomerDrawerTab(tab: string) {
    setDrawerTabInternal('customerDrawerTab', tab);
  }

  const handleRiskNotification = useCallback(
    (value: GlobalState['riskNotification']['value']) => {
      setRiskNotification(value);

      // Automatically close the notification after 3 seconds (optional)
      setTimeout(() => {
        setRiskNotification(null);
      }, 3000);
    },
    []
  );

  // Standalone POE-mode field, backed by the poeEnabled/setPOEEnabled state.
  // Memoized for stable identity so consumers can safely list it in
  // effect/binding deps (setPOEEnabled is a stable useState setter).
  const poeMode = useMemo(
    () => ({ enabled: poeEnabled, setEnabled: setPOEEnabled }),
    [poeEnabled]
  );

  return (
    <GlobalStateContext.Provider
      value={{
        lastVisitedUserId,
        setLastVisitedUserId,
        drawerState: {
          exposuresDrawerOpen,
          setExposuresDrawerOpen,
          notificationsDrawerOpen,
          notificationsDrawerFilterType,
          setNotificationsDrawerOpen,
          drawerOrder,
          agent,
          seed,
          vulnerability,
          integration,
          threatIntel,
          preseed,
          capability,
          customer,
          parkedDomain,
          agentDrawerKey,
          agentDrawerTab,
          assetDrawerTab,
          assetDrawerSubTab,
          assetDrawerKey,
          technologyDrawerKey,
          technologyDrawerTab,
          vulnerabilityDrawerTab,
          vulnerabilityDrawerKey,
          threatIntelDrawerKey,
          preseedDrawerKey,
          preseedDrawerTab,
          seedDrawerKey,
          seedDrawerTab,
          capabilityDrawerKey,
          capabilityDrawerTab,
          customerDrawerKey,
          customerDrawerTab,
          parkedDomainDrawerKey,
          setAgent,
          setAgentDrawerTab,
          setSeedDrawerTab,
          setSeed,
          handleOpenSeedDrawer,
          handleOpenAgentDrawer,

          setAssetDrawerTab,
          setTechnologyDrawerTab,
          setVulnerabilityDrawerTab,
          setVulnerability,
          setThreatIntel,
          setPreseed,
          setCapability,
          setCustomer,
          setCustomerDrawerTab,
          setParkedDomain,
          setIntegration,
          setIntegrationDrawerCategory: category => {
            setSearchParams(params => {
              params.set('integrationDrawerCategory', category);
              return params;
            });
          },

          handleOpenTechnologyDrawer,
          handleOpenAssetDrawer,
          handleOpenVulnerabilityDrawer,
          handleOpenIntegrationDrawer,
          handleOpenThreatIntelDrawer,
          handleOpenPreseedDrawer,
          handleOpenCapabilityDrawer,
          handleOpenCustomerDrawer,
          handleOpenParkedDomainDrawer,
        },
        dashboard: {
          period,
        },
        modal: {
          selectUserAgent: {
            assetKeys: selectUserAgentAssetKeys,
            onAssetKeysChange: setSelectUserAgentAssetKeys,
          },
          ticket: {
            associateTicket: {
              open: associateTicketOpen,
              onOpenChange: setAssociateTicketOpen,
              riskKeys: associateTicketRiskKeys,
              onRiskKeysChange: setAssociateTicketRiskKeys,
            },
            setup: { open: setupOpen, onOpenChange: setSetupOpen },
          },
          alert: { open: alertOpen, onOpenChange: setAlertOpen },
          upgrade: {
            open: upgradeOpen,
            onOpenChange: setUpgradeOpen,
          },
          seed: { open: seedOpen, onOpenChange: setSeedOpen },
          risk: {
            open: riskOpen,
            type: riskType,
            onChange: (open, type) => {
              setRiskOpen(open);
              setRiskType(type);
            },
            selectedAssets,
            onSelectedAssetsChange: setSelectedAssets,
            selectedRisks,
            onSelectedRisksChange: setSelectedRisks,
          },
          asset: { open: assetOpen, onOpenChange: setAssetOpen },
          file: { open: fileOpen, onOpenChange: setFileOpen },
          pushNotification: {
            open: pushNotificationOpen,
            onOpenChange: setPushNotificationOpen,
          },
          surfaceSetup: {
            open: surfaceSetupOpen,
            onOpenChange: setSurfaceSetupOpen,
          },
          allModal: { open: allModalOpen, onOpenChange: setAllModalOpen },
        },
        awsMarketplaceConfig: {
          value: awsMarketplaceConfig,
          onChange: setAwsMarketplaceConfig,
          verifyLinking,
          onVerifyLinkingChange: setVerifyLinking,
        },
        azureMarketplaceConfig: {
          value: azureMarketplaceConfig,
          onChange: setAzureMarketplaceConfig,
          verifyLinking: azureVerifyLinking,
          onVerifyLinkingChange: setAzureVerifyLinking,
        },
        riskNotification: {
          value: riskNotification,
          onChange: handleRiskNotification,
        },
        setupIntegration: {
          value: setupIntegration,
          onChange: setSetupIntegration,
        },
        poeMode,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};
